import IOrderBy from '../../model/IOrderBy';
import {runtimeEnv} from '../../lib/runtimeEnv';

const makeUrl = (baseUrl: string, page?: number, sort?: IOrderBy, params: string[] = [], limit = 0) => {
    const mutableParams = [...params];
    if (page) {
        mutableParams.push(`page=${page}`);
    }
    if (sort) {
        mutableParams.push(`order[${sort?.field}]=${sort?.direction}`);
    }
    if (limit) {
        mutableParams.push(`limit=${limit}`);
    }

    return mutableParams.length === 0
        ? baseUrl
        : `${baseUrl}?${mutableParams.join('&')}`;
};

const withIncidentDateFallback = (url: string, sort?: IOrderBy) =>
    sort?.field === 'resolved' ? `${url}&order[created_at]=desc` : url;

export const ApiUrls = {
    TOKEN_REFRESH: '/token/refresh',
    TOKEN: '/token',
    IMPERSONATE: (id: number) => `/impersonate/${id}`,
    EMAIL_RECONFIRM: '/users/email/confirm/send',
    USERS: (page?: number, sort?: IOrderBy, filters?: string[]) => makeUrl('/users', page, sort, filters),
    USER: (id: number) => `/users/${id}`,
    USER_DISABLE_RECURRING: '/users/disable-recurring',
    USER_TARIFF: (id: number) => `/users/${id}/tariff`,
    USER_BLOCKED: (id: number) => `/users/${id}/blocked`,
    USER_CONFIRM_EMAIL: (id: number) => `/users/${id}/confirm-email`,
    USER_UPDATE_EMAIL_STATUS: (id: number) => `/users/${id}/update-email-status`,
    USER_SEND_EMAIL_TEST_NOTIFY: (id: number) => `/users/${id}/send-email-test-notify`,
    USER_RESET_EMAIL_ERROR: (id: number) => `/users/${id}/reset-email-error`,
    USER_CHANGE_EMAIL: '/users/change-email',
    USER_SUBSCRIBE: '/users/subscribe-email/request',
    USER_REQUEST_DELETION: '/users/request-deletion',
    USER_CONFIRM_DELETION: (token: string) => `/users/confirm-deletion/${token}`,
    ME: '/users/me',
    PASSWORD_RESET_REQUEST: '/users/password-reset/request',
    PASSWORD_RESET_CONFIRM: (userId: number, token: string) => `/users/${userId}/password-reset/confirm/${token}`,
    EMAIL_CONFIRM: (userId: number, token: string) => `/users/${userId}/email/confirm/${token}`,
    CHANGE_EMAIL_CONFIRM: (userId: number, token: string) => `/users/${userId}/email/change-confirm/${token}`,
    SUMMARY_HOME: '/summary/home',
    SUMMARY_HTTP_MONITORS: '/summary/monitors',
    HTTP_MONITORS: (page?: number, sort?: IOrderBy, filters: string[]=[], limit = 0) =>
        makeUrl('/monitors', page, sort, filters, limit),
    HTTP_MONITORS_BATCH_ACTION: (action: string) => `/monitors/batch/${action}`,
    HTTP_MONITORS_BATCH_TAGS: '/monitors/batch/tags',
    HTTP_MONITORS_UNSUBSCRIBE: (uuid: string) => `/anon/monitors/unsubscribe/${uuid}`,
    UNSUBSCRIBE_FROM_EMAIL_NOTIFICATIONS_INFO: (id: string) =>
        `/anon/monitors/notifications/unsubscribe-email?id=${id}`,
    HTTP_MONITOR_UNSUBSCRIBE_FROM_EMAIL_NOTIFICATIONS_ACTION: (id: string, mode: number) =>
        `/anon/monitors/notifications/unsubscribe-email?id=${id}&mode=${mode}`,
    HTTP_MONITOR: (uuid: string) => `/monitors/${uuid}`,
    HTTP_MONITOR_RESULTS: (uuid: string, page?: number, filters?: string[]) =>
        makeUrl('/monitor-results', page, undefined, [`monitor.uuid=${uuid}`, ...(filters || [])]),
    HTTP_MONITOR_CHECK_NOW: (uuid: string): string => `/monitors/${uuid}/check-now`,
    HTTP_MONITOR_AVG_RESPONSE_TIME: (uuid: string) => `/monitors/${uuid}/avg-response-time`,
    HTTP_MONITOR_START: (uuid: string) => `/monitors/${uuid}/start`,
    HTTP_MONITOR_STOP: (uuid: string) => `/monitors/${uuid}/stop`,
    HTTP_INCIDENTS: (monitorUuid?: string, page?: number, sort?: IOrderBy, limit = 0) =>
        withIncidentDateFallback(
            makeUrl('/incidents', page, sort, monitorUuid ? [`monitor.uuid=${monitorUuid}`] : [], limit),
            sort,
        ),
    INCIDENTS_EXPORT: '/incidents/export',
    SUMMARY_HTTP_INCIDENTS: '/summary/incidents',
    HTTP_INCIDENTS_ALL: (page?: number, sort?: IOrderBy, filters: string[]=[]) =>
        withIncidentDateFallback(makeUrl('/incidents', page, sort, filters), sort),
    MONITORS_EXPORT: '/monitors/export',
    MONITORS_EXPORT_ALL: '/monitors/export/all',
    USERS_EXPORT: '/users/export',
    DOMAIN_INCIDENT: (id: number) => `/warnings/${id}`,
    SUMMARY_DOMAIN_INCIDENTS: '/summary/warnings',
    DOMAIN_INCIDENT_ALL: (page?: number, sort?: IOrderBy, filters: string[]=[], limit = 0) =>
        makeUrl('/warnings', page, sort, filters, limit),
    DOMAIN_INCIDENTS_EXPORT: '/warnings/export',
    DOMAIN_INCIDENT_DIFF: (id: number) => `/warnings/${id}/diff`,
    DOMAIN_INCIDENT_SAVE_EXPECTED: (id: number) => `/warnings/${id}/save-expected`,
    TARIFFS: (sort?: IOrderBy, filters: string[] = []) => makeUrl('/tariffs', 0, sort, filters),
    TARIFF: (tariffId: number) => `/tariffs/${tariffId}`,
    TARIFF_DEFAULT: (tariffId: number) => `/tariffs/${tariffId}/default`,
    // eslint-disable-next-line max-len
    PAYMENTS: (page?:number, sort?: IOrderBy, all?: boolean, filters: string[]=[]) => makeUrl('/payments', page, sort, all ? [...filters, 'all=true'] : filters),
    PAYMENT: (paymentId: number) => `/payments/${paymentId}`,
    PAYMENTS_MANUAL: '/payments/manual',
    PAYMENT_USER_STATS: () => '/payments/users/stats',
    PAYMENT_USER_STATS_PLOT_DATA: (year: number | null) => '/payments/users/stats/plot' + (year ? '?year=' + year : ''),
    LEGAL_INVOICE: '/legal-invoice-request',
    TELEGRAM_CHATS: (sort?: IOrderBy, params?: string[]) => makeUrl('/telegram-chats', 0, sort, params),
    TELEGRAM_CHAT: (id: number) => `/telegram-chats/${id}`,
    TELEGRAM_CHAT_SEND_TEST_NOTIFY: (id: number) => `/telegram-chats/test-notify/${id}`,
    MAX_CHATS: (sort?: IOrderBy, params?: string[]) => makeUrl('/max-chats', 0, sort, params),
    MAX_CHAT: (id: number) => `/max-chats/${id}`,
    MAX_CHAT_TEST_NOTIFY: (id: number) => `/max-chats/test-notify/${id}`,
    WEBHOOKS: (sort?: IOrderBy, params?: string[]) => makeUrl('/webhooks', 0, sort, params),
    WEBHOOK: (id: number) => `/webhooks/${id}`,
    WEBHOOK_TEST_NOTIFY: (id: number) => `/webhooks/test-notify/${id}`,
    ADDITIONAL_EMAILS: (sort?: IOrderBy, params?: string[]) => makeUrl('/additional-emails', 0, sort, params),
    ADDITIONAL_EMAIL: (id: number) => `/additional-emails/${id}`,
    ADDITIONAL_EMAIL_SEND_TEST_NOTIFY: (id: number) => `/additional-emails/send-test-notify/${id}`,
    ADDITIONAL_EMAILS_CONFIRMED: () => '/additional-email/confirm/send',
    ADDITIONAL_EMAIL_CONFIRM_GET: (token: string) => `/additional-emails/confirm/user/${token}`,
    BATCH_CHECK: (operationId: string) => `/batch/${operationId}`,
    HTTP_MONITORS_IDS: (sort?: IOrderBy, filters?: string[]) => makeUrl('/monitors/ids', 0, sort, filters),
    TARIFF_VARIANTS: (sort?: IOrderBy) => makeUrl('/tariff-variants', 0, sort),
    TARIFF_VARIANT: (id: number) => `/tariff-variants/${id}`,
    USER_CHANGE_NEWSLETTER_CONSENT: (id: number) => `/users/${id}/change-agree-to-newsletter`,
    ADMIN_DASHBOARD_METRICS: '/admin-dashboard-metrics',
    INCIDENTS_TYPE_STATS: '/incidents-type-stats',
    TAGS: '/tags',
    TAG: (id: string) => `/tags/${id}`,
    SUPPORT_REQUEST: '/support/request',
    OAUTH_INIT: (provider: string) => `/oauth2/init/${provider}`,
    OAUTH_FINISH: (provider: string) => `/oauth2/finish/${provider}`,
    ZONES: '/zones',
    COUNTRIES: (zoneId: number) => `/countries?zone.id=${zoneId}`,
    CITIES: (page?: number, sort?: IOrderBy, filters: string[] = []) => makeUrl('/cities', page, sort, filters),
    CITY: (id: number) => `/cities/${id}`,
    CITIES_BY_COUNTRY: (countryId: number) => `/cities?country.id=${countryId}`,
    SEARCH_CITIES: (countryId: number, query: string, limit = 5) =>
        makeUrl('/cities', undefined, undefined, [`country.id=${countryId}`, `name=${query}`, `limit=${limit}`]),
    LOCATIONS: (page?: number, sort?: IOrderBy, filters: string[] = []) => makeUrl('/locations', page, sort, filters),
    LOCATION: (id: number) => `/locations/${id}`,
    SEARCH_LOCATIONS: (cityId: number, query: string, limit = 5) =>
        makeUrl('/locations', undefined, undefined, [`city.id=${cityId}`, `name=${query}`, `limit=${limit}`]),
    STATUS_PAGES: (page?: number, sort?: IOrderBy, filters: string[] = []) =>
        makeUrl('/status-pages', page, sort, filters),
    STATUS_PAGE: (uuid: string) => `/status-pages/${uuid}`,
    STATUS_PAGE_CHECK_SLUG: (slug: string) => `/status-pages/check-slug?slug=${encodeURIComponent(slug)}`,
    STATUS_PAGE_VIEW: (slugOrUuid: string) => `/status-pages/view/${slugOrUuid}`,
    STATUS_PAGE_PDF: (slugOrUuid: string) => `/status-pages/view/${slugOrUuid}/pdf`,
    USER_REPORTS: (page?: number, sort?: IOrderBy, filters: string[] = []) =>
        makeUrl('/reports', page, sort, filters),
    USER_REPORT: (uuid: string) => `/reports/${uuid}`,
    USER_REPORT_VIEW: (uuid: string) => `/reports/view/${uuid}`,
    PROMO_CODES: (page?: number, sort?: IOrderBy, filters: string[] = []) =>
        makeUrl('/promo-codes', page, sort, filters),
    PROMO_CODE: (id: number) => `/promo-codes/${id}`,
    PROMO_CODE_ACTIVATE: '/promo-codes/activate',
    SERVERS: (page?: number, sort?: IOrderBy, filters: string[] = []) =>
        makeUrl('/servers', page, sort, filters),
    SERVERS_IDS: (sort?: IOrderBy, filters?: string[]) => makeUrl('/servers/ids', 0, sort, filters),
    SERVERS_BATCH_ACTION: (action: string) => `/servers/batch/${action}`,
    SERVER: (uuid: string) => `/servers/${uuid}`,
    SERVER_PAUSE: (uuid: string) => `/servers/${uuid}/pause`,
    SERVER_RESUME: (uuid: string) => `/servers/${uuid}/resume`,
    SERVER_REGENERATE_TOKEN: (uuid: string) => `/servers/${uuid}/regenerate-token`,
    SERVER_STATUS: (uuid: string) => `/servers/${uuid}/status`,
    SERVER_METRICS: (uuid: string) => `/servers/${uuid}/metrics`,
    SERVER_MOUNTS: (uuid: string) => `/servers/${uuid}/mounts`,
    SERVER_ALERT_RULES: (page?: number, sort?: IOrderBy, filters: string[] = [], limit = 0) =>
        makeUrl('/server-alert-rules', page, sort, filters, limit),
    SERVER_ALERT_RULE: (uuid: string) => `/server-alert-rules/${uuid}`,
    SERVER_ALERT_RULE_RECOMMENDATIONS: '/server-alert-rules/recommendations',
    SERVER_INCIDENTS: (page?: number, sort?: IOrderBy, filters: string[] = [], limit = 0) =>
        makeUrl('/server-incidents', page, sort, filters, limit),
    SERVER_INCIDENT: (uuid: string) => `/server-incidents/${uuid}`,
    DOMAIN_MONITORS: (page?: number, sort?: IOrderBy, filters: string[] = []) =>
        makeUrl('/domain-monitors', page, sort, filters),
    DOMAIN_MONITORS_IDS: (sort?: IOrderBy, filters?: string[]) => makeUrl('/domain-monitors/ids', 0, sort, filters),
    DOMAIN_MONITOR: (uuid: string) => `/domain-monitors/${uuid}`,
    SUMMARY_DOMAIN_MONITORS: '/summary/domain-monitors',
    DOMAIN_MONITORS_BATCH_ACTION: (action: string) => `/domain-monitors/batch/${action}`,
    DOMAIN_MONITORS_EXPORT: '/domain-monitors/export',
    DOMAIN_MONITORS_EXPORT_ALL: '/domain-monitors/export/all',
    PORT_MONITORS: (page?: number, sort?: IOrderBy, filters: string[] = [], limit = 0) =>
        makeUrl('/port-monitors', page, sort, filters, limit),
    PORT_MONITOR: (uuid: string) => `/port-monitors/${uuid}`,
    PORT_MONITOR_STOP: (uuid: string) => `/port-monitors/${uuid}/stop`,
    PORT_MONITOR_START: (uuid: string) => `/port-monitors/${uuid}/start`,
    PORT_MONITOR_CHECK_NOW: (uuid: string) => `/port-monitors/${uuid}/check-now`,
    PORT_MONITORS_IDS: (sort?: IOrderBy, filters?: string[]) => makeUrl('/port-monitors/ids', 0, sort, filters),
    PORT_MONITORS_BATCH_ACTION: (action: string) => `/port-monitors/batch/${action}`,
    PORT_MONITORS_EXPORT: '/port-monitors/export',
    SUMMARY_PORT_MONITORS: '/summary/port-monitors',
    SUMMARY_PORT_INCIDENTS: '/summary/port-incidents',
    PORT_INCIDENTS: (page?: number, sort?: IOrderBy, filters: string[] = [], limit = 0) =>
        makeUrl('/port-incidents', page, sort, filters, limit),
    PORT_INCIDENT: (uuid: string) => `/port-incidents/${uuid}`,
    PORT_INCIDENTS_EXPORT: '/port-incidents/export',
};


export const AppUrls = {
    HOME: '/',
    LOGIN: '/login',
    REGISTRATION: '/registration',
    PASSWORD_RESET: '/password/reset',
    PASSWORD_RESET_SENT: '/password/reset/sent',
    NEW_PASSWORD: '/password/reset/:userId/:token',
    EMAIL_CONFIRMATION: '/email/confirm/:userId/:token',
    CHANGE_EMAIL_CONFIRMATION: '/change-email/:userId/:token',
    ADDITIONAL_EMAIL_CONFIRM: '/additional-emails/confirm/:token',
    FORBIDDEN: '/forbidden',
    SETTINGS: '/settings',
    HTTP_MONITOR_UNSUBSCRIBE: '/anon/monitors/unsubscribe/:uuid',
    NOTIFICATIONS_UNSUBSCRIBE: '/unsubscribe-from-email/:id',
    HTTP_MONITORS: '/monitors',
    STATUS_PAGES: '/status-pages',
    STATUS: '/status/:slug',
    PAYMENTS: '/payments',
    TARIFFS: '/tariffs',
    PRICING: '/pricing',
    ADMIN_TARIFFS: '/admin/tariffs',
    ADMIN_TARIFF_VARIANTS: '/admin/tariff-variants',
    ADMIN_PAYMENTS: '/admin/payments',
    ADMIN_USERS: '/admin/users',
    ADMIN_REPORTS: '/admin/reports',
    ADMIN_CITIES: '/cities',
    ADMIN_LOCATIONS: '/locations',
    ADMIN_PROMO_CODES: '/admin/promo-codes',
    HTTP_MONITOR_LOGS: '/monitors/:monitorId/logs',
    HTTP_MONITOR_DETAILS: '/monitors/:monitorId/details',
    HTTP_INCIDENTS: '/incidents',
    DOMAIN_INCIDENTS: '/warnings',
    USER_REPORTS: '/reports',
    USER_REPORT_VIEW: '/report/:uuid',
    SUPPORT: 'https://overseer.observer/support',
    SUPPORT_TICKET: 'https://overseer.observer/support#support',
    NEWS: 'https://overseer.observer/#news',
    NEWS_TARIFF: 'https://overseer.observer/tpost/rkjyc5p5p1-uspeite-zakrepit-vigodnii-monitoring-po',
    MANUAL_MANAGEMENT: '/manual-management',
    PAYMENT_SUCCESS: '/payment/success',
    PAYMENT_CANCEL: '/payment/cancel',
    PRIVACY_POLICY: 'https://overseer.observer/privacy/',
    LICENSE_AGREEMENT: 'https://overseer.observer/license/',
    PAYMENT_OFFER: 'https://overseer.observer/license/#recurring',
    NOT_FOUND: '*',
    EMAIL_WARNING: '/email-warning',
    EMAIL_PROBLEM: '/email-problem',
    MAIN_OVERSEER: 'https://overseer.observer/',
    MAIN_BROTHER: 'https://brotherhood.software/',
    REGISTRY: 'https://reestr.digital.gov.ru/reestr/3181603/?sphrase_id=5892292',
    EMAIL_DELETE: '/email-delete',
    SUBSCRIPTION: 'https://overseer.observer/subscription',
    PRIVACY: `https://${runtimeEnv('REACT_APP_CONTENT_SITE_URL')}/privacy`,
    LICENSE: `https://${runtimeEnv('REACT_APP_CONTENT_SITE_URL')}/license`,
    INFO_EMAIL: `mailto:${runtimeEnv('REACT_APP_INFO_EMAIL')}`,
    CONSENT: 'https://overseer.observer/consent/',
    PROMOS: 'https://overseer.observer/promo',
    BROTHERHOOD: 'https://brotherhood.software/#visit',
    HTTP_MONITOR_CORRECT: '/monitors?order=id&dir=desc&status=active&ok=true&days=0&page=1',
    HTTP_MONITOR_PROBLEM: '/monitors?order=id&dir=desc&status=active&ok=false&days=0&page=1',
    HTTP_MONITOR_PAUSE: '/monitors?order=id&dir=desc&status=paused&days=0&page=1',
    USER_CONFIRM_DELETION: '/users/confirm-deletion/:token',
    YANDEX_CAPTCHA_NOTICE: 'https://yandex.ru/legal/smartcaptcha_notice/ru/ ',
    TELEGRAM: `https://t.me/${runtimeEnv('REACT_APP_SUPPORT_TELEGRAM')}`,
    MAX_BOT: 'https://max.ru/id5018212950_bot',
    TELEGRAM_BOT: `https://t.me/${runtimeEnv('REACT_APP_TELEGRAM_BOT_NAME')}`,
    SUPPORT_VK: 'https://vk.ru/overseer.observer',
    SUPPORT_MAX: 'https://max.ru/u/f9LHodD0cOKqAHJ3sWFIm_I1DB_7LhD9XDUGB7yZRRbHT7qfQ47j0N0HF6E',
    SERVERS: '/servers',
    SERVER_DETAILS: '/servers/:serverId/details',
    SERVER_INCIDENTS: '/server-incidents',
    DOMAIN_MONITORS: '/domain-monitors',
    DOMAIN_MONITOR_DETAILS: '/domain-monitors/:domainMonitorId/details',
    PORT_MONITORS: '/port-monitors',
    PORT_MONITOR_DETAILS: '/port-monitors/:portMonitorId/details',
    PORT_INCIDENTS: '/port-incidents',
};

export const ROBOKASSA_PAYMENT_PAGE = (invoiceId: string) => `https://auth.robokassa.ru/Merchant/Index/${invoiceId}`;

export const TELEGRAM_CHATS_RESOURCE_NAME = 'telegram-chats';
export const MAX_CHATS_RESOURCE_NAME = 'max-chats';
export const ADDITIONAL_EMAILS_RESOURCE_NAME = 'additional-emails';
export const WEBHOOKS_RESOURCE_NAME = 'webhooks';